import {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';

import CustomAccordion from '../common/CustomAccordion';
import FileUploadInput from '../common/FileUploadInput';
import PatientSummary from './PatientSummary';
import PatientDetailsSection from './PatientDetailsSection';
import PatientFileItem from './PatientFileItem';
import {VIEW_PATIENT_SCREEN_NAME} from "../../config/constants";
import BackButton from "../common/BackButton";
import './ViewPatientScreen.scss';

const defaultCollapse = sections => sections.reduce(
  (acc, next) => ({...acc, [next.key]: false}),
  {}
);

const handleToggleAll = ({ setIsOpen, sections, open = true}) => e => {
  const dict = sections.reduce((acc, next) => ({
    ...acc,
    [next.key]: open,
  }), {});
  return setIsOpen(dict);
};

const handleToggleSection = ({isOpen, setIsOpen, open = true}) => field => e => {
  const dict = {...isOpen, [field]: open};
  return setIsOpen(dict);
};

const formatFileName = patient => {
  let str = patient?.user.first_name?.[0];
  str += '.';
  str += ` ${patient?.user.last_name}`;
  str += ' Home Exercise Program';
  str += ' ' + moment().format('M/D/YYYY');
  return str;
};

const ViewPatientScreen = props => {
  const [isOpen, setIsOpen] = useState(defaultCollapse(props.sections));
  const {patient: p, sections, fields, user} = props;
  const onOpenSection = handleToggleSection({isOpen, setIsOpen, open: true});
  const onCloseSection = handleToggleSection({isOpen, setIsOpen, open: false});
  const isProvider = user?.is_provider;
  return (
    <Box className='ViewPatientScreen'>
      <Box className='ViewPatientScreen__content'>
        {
          user.is_provider
          ?
            <Box>
              <Box marginBottom={2}>
                <BackButton />
              </Box>
              <PatientSummary patient={p} hideDetails={true} showCases={isProvider}/>
            </Box>
          : null
        }
        <Stack direction='row' flexWrap="wrap" justifyContent='space-between'>
          <Stack justifyContent='center'>
            {
              user.is_provider
                ? null
                :
                <Box marginBottom={2}>
                  <BackButton />
                </Box>
            }
          </Stack>
          <Stack direction='row' flexWrap="wrap" justifyContent='end' padding='1.5rem 0 0.2rem 0'>
            <Button onClick={handleToggleAll({sections, setIsOpen, open: false})}>
              <ExpandLessIcon/>
              Collapse All
            </Button>
            <Button onClick={handleToggleAll({sections, setIsOpen, open: true})}>
              <ExpandMoreIcon />
              Expand All
            </Button>
          </Stack>
        </Stack>
        <Stack justifyContent='center'>
          {sections.map(s => {
            if (s.isForm && (!s.hideFromPatient || props.isProvider)) {
              return (
                <PatientDetailsSection
                  key={s.key}
                  title={s.title}
                  fields={fields.filter(f => f.section === s.key)}
                  hasUnsavedChanges={!!Object.keys(props.updates)
                    .find(key => fields
                        .filter(f => f.section === s.key)
                        .map(f => f.key)
                        .includes(key)
                    )
                  }
                  isOpen={isOpen[s.key]}
                  onOpen={onOpenSection(s.key)}
                  onClose={onCloseSection(s.key)}
                  onFieldChange={props.onFieldChange}
                  onSave={props.onSave}
                />
              );
            }
            if (s.key === 'files') {
              return (
                <CustomAccordion
                  key={s.key}
                  title={s.title}
                  isOpen={isOpen[s.key]}
                  onOpen={onOpenSection(s.key)}
                  onClose={onCloseSection(s.key)}
                  boxShadow
                >
                  <Stack padding={{xs: 0, sm: 2, md: 3}} spacing={1}>
                    <FileUploadInput
                      label='Upload a File'
                      fileType='patient_file'
                      patientId={p.id}
                      allowTitle={true}
                      allowDescription={false}
                      titlePlaceholder={formatFileName(p)}
                      titleHelperText='Patient First Initial. Patient Last Name. Title of Document. Date'
                      screen={VIEW_PATIENT_SCREEN_NAME}
                    />
                    {(props.files || []).map(f =>
                      <PatientFileItem patientFile={f} />
                    )}
                  </Stack>
                </CustomAccordion>
              );
            }
          })}
        </Stack>
      </Box>
    </Box>
  );
};

ViewPatientScreen.propTypes = {
  patient: PropTypes.object,
  files: PropTypes.arrayOf(PropTypes.object),
  sessions: PropTypes.arrayOf(PropTypes.object),
  isProvider: PropTypes.bool,
};

export default ViewPatientScreen;
